import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, CalendarDays, MapPin, Mountain, MessageCircle, Moon, Sunrise, Tent, Users, Backpack, Thermometer, Footprints, Check, X, Tag, Bus, ShieldCheck } from "lucide-react";
import "@/pages/DeoriaTaal.css";
import { TripCountdown } from "@/pages/TripCountdown";
import { ShareTrip } from "@/pages/ShareTrip";
import { TripFaq } from "@/pages/TripFaq";
import { TripGallery } from "@/pages/TripGallery";
import { InterestForm } from "@/pages/InterestForm";

const WHATSAPP = "https://wa.me/919871906396?text=" + encodeURIComponent("Hi Priyanka! I'd like to book the Deoria Taal getaway (21-25 Nov 2026).");

const itinerary = [
  { day: "01", date: "Sat 21 Nov", title: "Delhi → Rishikesh", body: "Leave the city behind. Overnight drive, Ganga aarti if we arrive in time, and a slow evening settling into mountain mode.", icon: Footprints },
  { day: "02", date: "Sun 22 Nov", title: "Rishikesh → Sari village", body: "A scenic road day along the Alaknanda and Mandakini. Sunset yoga on a terrace above the valley, home-cooked pahadi dinner.", icon: MapPin },
  { day: "03", date: "Mon 23 Nov", title: "Sari → Deoria Taal", body: "A gentle 2.5 km trek through oak and rhododendron to the lake. Afternoon by the water, evening sharing circle under the stars.", icon: Tent },
  { day: "04", date: "Tue 24 Nov", title: "Deoria Taal → Chopta · Tungnath", body: "Sunrise with Chaukhamba mirrored in the lake. Drive to Chopta, trek to Tungnath - the highest Shiva temple - and Chandrashila if legs allow.", icon: Mountain },
  { day: "05", date: "Wed 25 Nov", title: "Chopta → Rishikesh → Delhi", body: "Closing circle with chai and the mountains. Begin the descent home, carrying a little of the quiet back with us.", icon: Sunrise },
];

const highlights = [
  { icon: Sunrise, title: "Chaukhamba at dawn", body: "Snow peaks reflected in a mirror-still lake. The reason people come." },
  { icon: Mountain, title: "Tungnath & Chandrashila", body: "A sacred climb to 3,680 m, with a 360-degree Himalayan skyline." },
  { icon: Moon, title: "Stargazing nights", body: "No light pollution. The Milky Way over the water is unreal." },
  { icon: Users, title: "Daily yoga & circles", body: "Morning practice, evening sharing circles - held gently by Priyanka." },
];

const packing = [
  { icon: Thermometer, text: "Warm layers: nights drop to around 0 °C" },
  { icon: Backpack, text: "A daypack, water bottle & headlamp" },
  { icon: Footprints, text: "Broken-in trekking shoes" },
  { icon: Users, text: "An open heart & a yoga mat" },
];

const included = ["Stay for 4 nights (tents & homestays)", "All meals - pure vegetarian", "Transport from the pickup point & back", "Experienced trek guide", "Forest permits & entry fees", "Daily yoga & evening sharing circles", "First-aid kit & basic medical support"];
const excluded = ["Personal expenses & shopping", "Flights or train tickets to the pickup point", "Anything not listed under inclusions"];
const rules = ["No alcohol", "No drugs", "No smoking", "No loud music", "No weapons"];

export default function DeoriaTaal() {
  useEffect(() => {
    document.title = "Deoria Taal Trek · 21-25 Nov 2026 · Travel with Advaita";
    window.scrollTo(0, 0);
    return () => { document.title = "Priyanka Pahwa · Yoga & Sharing Circles"; };
  }, []);

  return (
    <div className="dt" data-testid="deoria-page">
      <header className="dt-top">
        <Link to="/" className="dt-back" data-testid="deoria-back-link"><ArrowLeft size={16} /> Priyanka Pahwa</Link>
        <div className="dt-top-actions">
          <ShareTrip compact />
          <a href={WHATSAPP} target="_blank" rel="noopener noreferrer" className="dt-top-cta" data-testid="deoria-top-whatsapp"><MessageCircle size={16} /> WhatsApp</a>
        </div>
      </header>

      <section className="dt-hero">
        <img src="/images/deoria-hero.jpg" alt="Chaukhamba peaks reflected in Deoria Taal" className="dt-hero-img" data-testid="deoria-hero-image" />
        <div className="dt-hero-copy">
          <p className="dt-kicker dt-rise">Travel with Advaita · 5-day getaway</p>
          <h1 className="dt-rise d1" data-testid="deoria-title">Deoria Taal Trek</h1>
          <p className="dt-script dt-rise d2">Chaos se door... pure mountain mode ON!</p>
          <div className="dt-date-pill dt-rise d3" data-testid="deoria-date"><CalendarDays size={18} /> 21 – 25 November 2026</div>
          <TripCountdown />
        </div>
        <div className="dt-hero-band">
          <div className="dt-brush">Choose your escape</div>
          <div className="dt-navy">
            <span className="dt-navy-big">5 Days · 4 Nights</span>
            <span className="dt-navy-small">Sari village · Deoria Taal · Chopta · Tungnath</span>
          </div>
        </div>
      </section>

      <section className="dt-facts" data-testid="deoria-facts">
        <div><span>Dates</span><strong>21 – 25 Nov 2026</strong></div>
        <div><span>Where</span><strong>Rudraprayag, Uttarakhand</strong></div>
        <div><span>Altitude</span><strong>2,438 m lake · 3,680 m temple</strong></div>
        <div><span>Grade</span><strong>Easy – moderate</strong></div>
        <div><span>Pricing</span><strong>DM for pricing · early-bird & group offers</strong></div>
      </section>

      <section className="dt-intro">
        <div className="dt-intro-copy">
          <p className="dt-kicker">Why this trip</p>
          <h2>Five days where the mountains do the talking.</h2>
          <p>Deoria Taal is a small emerald lake that holds the entire Chaukhamba massif in its reflection. We walk there slowly, practise yoga on its shore, sit in circle under a sky full of stars, and climb to Tungnath before we return. No rush, no itinerary anxiety - just presence, movement and good company.</p>
          <a href={WHATSAPP} target="_blank" rel="noopener noreferrer" className="dt-btn" data-testid="deoria-intro-whatsapp"><MessageCircle size={18} /> Book your escape now</a>
        </div>
        <figure className="dt-intro-img">
          <img src="/images/deoria-yoga.jpg" alt="Morning meditation on the lakeshore at sunrise" />
          <figcaption>Morning practice, lakeside.</figcaption>
        </figure>
      </section>

      <section className="dt-itinerary" id="itinerary" data-testid="deoria-itinerary">
        <div className="dt-section-head">
          <p className="dt-kicker">The plan</p>
          <h2>5-day itinerary</h2>
          <p className="dt-muted">A relaxed pace with room to breathe. Final timings shared on WhatsApp before departure.</p>
        </div>
        <ol className="dt-days">
          {itinerary.map((d) => (
            <li key={d.day} className="dt-day" data-testid={`deoria-day-${d.day}`}>
              <div className="dt-day-num">Day {d.day}<small>{d.date}</small></div>
              <div className="dt-day-body">
                <h3><d.icon size={18} /> {d.title}</h3>
                <p>{d.body}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      <section className="dt-details" id="details" data-testid="deoria-details">
        <div className="dt-section-head">
          <p className="dt-kicker">Trip details</p>
          <h2>Everything you need to know</h2>
        </div>
        <div className="dt-details-grid">
          <article className="dt-price-card" data-testid="deoria-pricing-card">
            <p className="dt-kicker"><Tag size={14} /> Pricing</p>
            <h3>DM for pricing</h3>
            <p>Early-bird and group discounts available - message us for the current rate and to hold your spot.</p>
            <ul className="dt-mini-facts">
              <li><Bus size={16} /><span><strong>Pickup</strong> Delhi · exact point & time shared on WhatsApp after booking</span></li>
              <li><ShieldCheck size={16} /><span><strong>Booking</strong> Seats are confirmed on advance payment; cancellation terms shared on request</span></li>
            </ul>
            <a href={WHATSAPP} target="_blank" rel="noopener noreferrer" className="dt-btn dt-btn-yellow" data-testid="deoria-pricing-whatsapp"><MessageCircle size={18} /> Ask for pricing</a>
          </article>
          <div className="dt-lists">
            <div className="dt-list" data-testid="deoria-inclusions">
              <h3>What&#39;s included</h3>
              <ul>{included.map((t) => <li key={t}><Check size={16} /> {t}</li>)}</ul>
            </div>
            <div className="dt-list is-excluded" data-testid="deoria-exclusions">
              <h3>Not included</h3>
              <ul>{excluded.map((t) => <li key={t}><X size={16} /> {t}</li>)}</ul>
            </div>
            <div className="dt-list is-rules" data-testid="deoria-rules">
              <h3>House rules</h3>
              <p className="dt-muted">We keep the space clean, quiet and safe for everyone.</p>
              <ul className="dt-rules">{rules.map((t) => <li key={t}>{t}</li>)}</ul>
            </div>
          </div>
        </div>
      </section>

      <TripGallery />

      <section className="dt-highlights" data-testid="deoria-highlights">
        <div className="dt-section-head">
          <p className="dt-kicker">Highlights</p>
          <h2>What you'll carry home</h2>
        </div>
        <div className="dt-high-grid">
          {highlights.map((h) => (
            <article key={h.title} className="dt-high">
              <h.icon size={26} />
              <h3>{h.title}</h3>
              <p>{h.body}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="dt-pack" data-testid="deoria-packing">
        <div className="dt-section-head">
          <p className="dt-kicker">Good to know</p>
          <h2>What to pack</h2>
        </div>
        <ul>
          {packing.map((p) => <li key={p.text}><p.icon size={18} /> {p.text}</li>)}
        </ul>
        <p className="dt-muted">Group size is kept small. Transport from Delhi, stays and meals are discussed on WhatsApp - just ask.</p>
      </section>

      <TripFaq />

      <InterestForm />

      <section className="dt-cta" data-testid="deoria-cta">
        <img src="/images/deoria-hero.jpg" alt="" aria-hidden="true" className="dt-cta-bg" />
        <div className="dt-cta-copy">
          <p className="dt-script">Travel with</p>
          <h2>Advaita</h2>
          <p className="dt-cta-date"><CalendarDays size={18} /> 21 – 25 Nov 2026 · DM for pricing</p>
          <a href={WHATSAPP} target="_blank" rel="noopener noreferrer" className="dt-btn dt-btn-yellow" data-testid="deoria-cta-whatsapp"><MessageCircle size={20} /> DM / WhatsApp for bookings</a>
          <ShareTrip />
          <Link to="/" className="dt-back-home" data-testid="deoria-home-link">← Back to priyankapahwa</Link>
        </div>
      </section>
    </div>
  );
}
